/**
 * CommonApp.ts
 *
 * Common logic for entrypoints.
 */

import "./Polyfills/Promise";

import { TelemetrySink } from "@microsoft/oteljs";
import { initialize as personalizationInit } from "@ms-ofb/officepersonalization/dist/lib/Common/Init";
import * as Configuration from "./Configuration/Configuration";
import * as Logging from "./Logging/Logging";
import Version from "./Version";
import * as Window from "./Window/Window";

const APP_NAME = "OfficeFeedbackSDK";

/**
 * Has the SDK been initialized
 */
let initialized = false;

/**
 * Has the SDK allow AADC
 */
let commonPolicyAllowAADC = false;

/**
 * Initialize
 */
export function initialize(): void {
	if (initialized) {
		return;
	}

	if (!Window.get().OfficeBrowserFeedback.initOptions) {
		throw new Error("Window.OfficeBrowserFeedback.initOptions not set");
	}

	Window.get().OfficeBrowserFeedback.sdkVersion = Version;
	Configuration.get().setCommonInitOptions(Window.get().OfficeBrowserFeedback.initOptions);
	commonPolicyAllowAADC = Configuration.get().isFeedbackSurveyEnabledByAADC();

	Logging.initialize(
		Configuration.get().getCommonInitOptions(),
		APP_NAME,
		Version,
		Configuration.get().getSdkSessionId(),
		Configuration.get().getEventSampling()
	);

	personalizationInit(Configuration.get().getPersonalizationInitOptions())

	initialized = true;

	if (!commonPolicyAllowAADC) {
		Logging.getLogger().logEvent(Logging.EventIds.FEEDBACK_SURVEYSDISABLED_AADC,
			Logging.LogLevel.Critical,
			{
				Data: JSON.stringify({
					ageGroup: Configuration.get().getCommonInitOptions().ageGroup,
					authenticationType: Configuration.get().getCommonInitOptions().authenticationType,
				}),
			});
	}
}

/**
 * Reset the module. Used in unit tests.
 */
export function reset() {
	initialized = false;
}

/**
 * Return Flag to determine whether Feedback and Survey is enabled by AADC.
 * Return true if Feedback and Survey is enabled.
 * Otherwise, return false.
 */
export function isFeedbackSurveyEnabledByAADC(): boolean {
	return commonPolicyAllowAADC;
}

/**
 * Update the telemetrySink during runtime.
 */
export function updateTelemetrySink(sink: TelemetrySink) {
	try {
		Configuration.get().updateTelemetrySink(sink);
		Logging.getLogger().flushQueue();

		// Re-init the logger with new sink
		Logging.initialize(
			Configuration.get().getCommonInitOptions(),
			APP_NAME,
			Version,
			Configuration.get().getSdkSessionId(),
			Configuration.get().getEventSampling()
		);
	} catch (e) {
		throw new Error("Failed to update telemetry sink: " + e);
	}
}


Window.setIsFeedbackSurveyEnabledByAADC(isFeedbackSurveyEnabledByAADC);
Window.setUpdateTelemetrySink(updateTelemetrySink);